import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type {
  AdminQuizCampaign,
  QuizQuestionAdmin,
  QuizSurveyQuestion,
  QuizTimerMode,
  QuizSession,
} from '../types';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Clock,
  Play,
  XCircle,
} from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { Layout } from '../components/layout/Layout';
import { QuizQuestionMediaView } from '../components/quiz/QuizQuestionMediaView';
import { cn } from '../lib/utils';
import { apiFetch } from '../lib/api';
import { createOptionOrders, createShuffledOrder } from '../lib/quiz-play-storage';

// ── Types ──────────────────────────────────────────────────────────────

interface AdminQuizSession extends QuizSession {
  questionCount: number;
  createdAt: string;
  updatedAt: string;
}

interface CampaignDetailResponse {
  ok: boolean;
  campaign: AdminQuizCampaign;
  sessions: AdminQuizSession[];
}

interface QuizPreviewResponse {
  ok: boolean;
  preview: true;
  campaign: AdminQuizCampaign;
  session: QuizSession;
  timerSeconds: number;
  timerMode: QuizTimerMode;
  roundTimerSeconds: number;
  questionTimerSeconds?: number | null;
  questions: QuizQuestionAdmin[];
}

type PageState = 'loading' | 'error' | 'ready' | 'playing' | 'review' | 'complete';

// ── Helpers ────────────────────────────────────────────────────────────

function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${`${s}`.padStart(2, '0')}`;
}

// ── Component ──────────────────────────────────────────────────────────

export function QuizPreviewPage() {
  const { campaignId, sessionId: urlSessionId } = useParams<{
    campaignId: string;
    sessionId?: string;
  }>();
  const navigate = useNavigate();

  const isCampaignMode = !urlSessionId;

  // Data
  const [campaign, setCampaign] = useState<AdminQuizCampaign | null>(null);
  const [allSessions, setAllSessions] = useState<AdminQuizSession[]>([]);
  const [currentSession, setCurrentSession] = useState<QuizSession | null>(null);
  const [questions, setQuestions] = useState<QuizQuestionAdmin[]>([]);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [timerMode, setTimerMode] = useState<QuizTimerMode>('per_round');

  // Page state
  const [pageState, setPageState] = useState<PageState>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Quiz play state
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [displayOrder, setDisplayOrder] = useState<number[]>([]);
  const [optionOrders, setOptionOrders] = useState<number[][]>([]);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const [quizFinished, setQuizFinished] = useState(false);
  const startTimeRef = useRef<number>(0);
  const questionStartedAtRef = useRef<number[]>([]);
  const [durationSeconds, setDurationSeconds] = useState(0);
  const [surveyAnswer, setSurveyAnswer] = useState<number | null>(null);

  // Campaign mode: track which round index we are on
  const [campaignRoundIndex, setCampaignRoundIndex] = useState(0);

  // Sorted sessions for campaign mode
  const sortedSessions = useMemo(
    () => [...allSessions].sort((a, b) => a.sortOrder - b.sortOrder),
    [allSessions],
  );
  const scoredQuestions = useMemo(
    () => questions.filter((question) => question.kind !== 'survey'),
    [questions],
  );
  const surveyQuestion = useMemo<QuizSurveyQuestion | null>(() => {
    const found = questions.find((question) => question.kind === 'survey');
    return found ? { id: found.id, kind: 'survey', question: found.question, options: found.options, media: found.media } : null;
  }, [questions]);

  // Playing state shows questions/options shuffled for an authentic member-like
  // experience; answers stay keyed to the original (saved) order so the review
  // screen below can show everything in actual saved order for verification.
  const displayIndex = displayOrder[currentQuestionIndex] ?? currentQuestionIndex;
  const currentOptionOrder =
    optionOrders[displayIndex] ??
    Array.from({ length: scoredQuestions[displayIndex]?.options.length ?? 0 }, (_, i) => i);

  // ── Fetch helpers ──────────────────────────────────────────────────

  const fetchRoundPreview = useCallback(
    async (sid: string) => {
      if (!campaignId) return;
      setPageState('loading');
      setErrorMsg(null);
      try {
        const data = await apiFetch<QuizPreviewResponse>(
          `/api/admin/quiz-campaigns/${encodeURIComponent(campaignId)}/sessions/${encodeURIComponent(sid)}/preview`,
        );
        setCampaign(data.campaign);
        setCurrentSession(data.session);
        setQuestions(data.questions);
        setTimerSeconds(data.timerSeconds);
        setTimerMode(data.timerMode);

        if (data.questions.filter((question) => question.kind !== 'survey').length === 0) {
          setErrorMsg('This round has no scored questions.');
          setPageState('error');
          return;
        }

        setSurveyAnswer(null);
        setPageState('ready');
      } catch (e) {
        setErrorMsg(e instanceof Error ? e.message : 'Failed to load preview');
        setPageState('error');
      }
    },
    [campaignId],
  );

  // ── Initial load ───────────────────────────────────────────────────

  useEffect(() => {
    if (!campaignId) return;

    if (isCampaignMode) {
      // Campaign mode: fetch campaign detail, then auto-select first round
      void (async () => {
        setPageState('loading');
        try {
          const data = await apiFetch<CampaignDetailResponse>(
            `/api/admin/quiz-campaigns/${encodeURIComponent(campaignId)}`,
          );
          setCampaign(data.campaign);
          setAllSessions(data.sessions);

          const sorted = [...data.sessions].sort((a, b) => a.sortOrder - b.sortOrder);
          if (sorted.length === 0) {
            setErrorMsg('This campaign has no rounds.');
            setPageState('error');
            return;
          }

          setCampaignRoundIndex(0);
          await fetchRoundPreview(sorted[0].id);
        } catch (e) {
          setErrorMsg(e instanceof Error ? e.message : 'Failed to load campaign');
          setPageState('error');
        }
      })();
    } else {
      // Round mode: fetch just that round
      void fetchRoundPreview(urlSessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, urlSessionId, isCampaignMode]);

  // ── Timer ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (pageState !== 'playing' || secondsLeft === null) return;
    if (secondsLeft <= 0) {
      if (timerMode === 'per_round') {
        setQuizFinished(true);
      }
      return;
    }
    const id = window.setInterval(() => {
      setSecondsLeft((prev) => (prev === null ? prev : Math.max(0, prev - 1)));
    }, 1000);
    return () => window.clearInterval(id);
  }, [pageState, secondsLeft, timerMode]);

  // ── Auto-finish when timer hits 0 ─────────────────────────────────

  useEffect(() => {
    if (pageState === 'playing' && quizFinished) {
      finishQuiz();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizFinished, pageState]);

  // ── Actions ────────────────────────────────────────────────────────

  const startQuiz = () => {
    setCurrentQuestionIndex(0);
    setAnswers([]);
    setDisplayOrder(createShuffledOrder(scoredQuestions.length));
    setOptionOrders(createOptionOrders(scoredQuestions));
    setQuizFinished(false);
    setDurationSeconds(0);
    setSurveyAnswer(null);
    startTimeRef.current = Date.now();
    questionStartedAtRef.current = [Date.now()];
    setSecondsLeft(timerSeconds);
    setPageState('playing');
  };

  const advanceQuestion = useCallback(() => {
    if (currentQuestionIndex < scoredQuestions.length - 1) {
      window.setTimeout(() => {
        setCurrentQuestionIndex((prev) => prev + 1);
        if (timerMode === 'per_question') {
          questionStartedAtRef.current = [...questionStartedAtRef.current, Date.now()];
          setSecondsLeft(timerSeconds);
        }
      }, 300);
      return;
    }
    setQuizFinished(true);
  }, [currentQuestionIndex, scoredQuestions.length, timerMode, timerSeconds]);

  const handleAnswerSelect = (optionIndex: number) => {
    const newAnswers = [...answers];
    newAnswers[displayIndex] = optionIndex;
    setAnswers(newAnswers);
    advanceQuestion();
  };

  useEffect(() => {
    if (pageState !== 'playing' || timerMode !== 'per_question' || secondsLeft !== 0 || quizFinished) return;
    advanceQuestion();
  }, [advanceQuestion, pageState, quizFinished, secondsLeft, timerMode]);

  const finishQuiz = () => {
    const elapsed =
      timerMode === 'per_question'
        ? questionStartedAtRef.current.reduce((total, startedAt, idx, arr) => {
            const endedAt = arr[idx + 1] ?? Date.now();
            return total + Math.max(0, Math.round((endedAt - startedAt) / 1000));
          }, 0)
        : Math.round((Date.now() - startTimeRef.current) / 1000);
    setDurationSeconds(elapsed);
    setPageState('review');
  };

  const handleNextRound = async () => {
    const nextIdx = campaignRoundIndex + 1;
    if (nextIdx >= sortedSessions.length) {
      setPageState('complete');
      return;
    }
    setCampaignRoundIndex(nextIdx);
    await fetchRoundPreview(sortedSessions[nextIdx].id);
  };

  const handleBackToAdmin = () => {
    void navigate('/admin');
  };

  // ── Scoring ────────────────────────────────────────────────────────

  const { totalPoints, maxPoints } = useMemo(() => {
    let total = 0;
    let max = 0;
    for (let i = 0; i < scoredQuestions.length; i++) {
      const q = scoredQuestions[i];
      max += q.points;
      if (answers[i] === q.correctAnswer) {
        total += q.points;
      }
    }
    return { totalPoints: total, maxPoints: max };
  }, [scoredQuestions, answers]);

  const isLastRound = campaignRoundIndex >= sortedSessions.length - 1;

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Preview mode banner */}
        <div
          className="flex items-start gap-3 p-4 rounded-[var(--radius-lg)] border border-[var(--color-warning)] bg-[var(--color-warning-subtle)]"
        >
          <AlertTriangle className="w-5 h-5 text-[var(--color-warning)] shrink-0 mt-0.5" />
          <p className="text-sm text-[var(--color-text-primary)] leading-relaxed">
            <span className="font-semibold">Preview Mode</span> — Answers are not recorded.
            Correct answers are revealed after submission for verification only.
            Members will not see correct answers.
          </p>
        </div>

        {/* Main content card */}
        <div className="bg-[var(--color-surface-1)] p-6 md:p-8 rounded-[var(--radius-xl)] border border-[var(--color-border)]">
          <div className="max-w-2xl mx-auto text-center">
            {/* ── Loading ── */}
            {pageState === 'loading' && (
              <p className="text-[var(--color-text-secondary)] text-sm">Loading preview…</p>
            )}

            {/* ── Error ── */}
            {pageState === 'error' && (
              <>
                <XCircle className="w-10 h-10 mx-auto mb-4 text-[var(--color-error)]" />
                <h3 className="text-xl font-semibold text-[var(--color-text-primary)] mb-4">
                  Preview Error
                </h3>
                <p className="text-[var(--color-text-secondary)] text-sm mb-6">
                  {errorMsg ?? 'Something went wrong.'}
                </p>
                <button
                  type="button"
                  onClick={handleBackToAdmin}
                  className="inline-flex items-center gap-2 text-sm font-medium text-[var(--color-accent)] hover:underline"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Admin
                </button>
              </>
            )}

            {/* ── Ready ── */}
            {pageState === 'ready' && campaign && currentSession && (
              <>
                <h3 className="text-xl font-semibold text-[var(--color-text-primary)] mb-1">
                  {campaign.title}
                </h3>
                <p className="text-[var(--color-accent)] text-sm font-medium mb-4">
                  {currentSession.label}
                </p>
                <p className="text-[var(--color-text-secondary)] text-sm mb-6 leading-relaxed">
                  {scoredQuestions.length} scored question{scoredQuestions.length === 1 ? '' : 's'} · {timerMode === 'per_question' ? `${formatCountdown(timerSeconds)} per question` : `${formatCountdown(timerSeconds)} for the round`}
                  {surveyQuestion ? ' · optional survey after submit' : ''}
                </p>
                <button
                  type="button"
                  onClick={startQuiz}
                  className="inline-flex items-center gap-2 bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] text-white font-semibold py-3 px-6 rounded-[var(--radius-md)] transition-colors duration-[var(--duration-fast)]"
                >
                  <Play className="w-4 h-4" />
                  Start Preview
                </button>
              </>
            )}

            {/* ── Playing ── */}
            {pageState === 'playing' && (
              <div className="text-left space-y-8 animate-in fade-in zoom-in duration-300">
                <div className="flex justify-between items-center mb-8 gap-4">
                  <span className="text-xs font-medium text-[var(--color-accent)]">
                    Question {currentQuestionIndex + 1} of {scoredQuestions.length}
                  </span>
                  <span className="text-xs font-semibold tabular-nums text-[var(--color-text-primary)]">
                    {secondsLeft !== null ? formatCountdown(secondsLeft) : '--:--'}
                  </span>
                </div>
                <p className="text-xs text-[var(--color-text-tertiary)] -mt-5">
                  {timerMode === 'per_question' ? 'Timer resets every question in preview too.' : 'One timer applies to the full round in preview.'}
                </p>

                <div className="w-full h-[3px] bg-[var(--color-surface-2)] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[var(--color-accent)] transition-all duration-300 rounded-full"
                    style={{
                      width: `${((currentQuestionIndex + 1) / scoredQuestions.length) * 100}%`,
                    }}
                  />
                </div>

                <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-2 leading-tight">
                  {scoredQuestions[displayIndex]?.question}
                </h3>
                <QuizQuestionMediaView media={scoredQuestions[displayIndex]?.media} />
                <p className="text-xs text-[var(--color-text-tertiary)] mb-6">
                  Worth {scoredQuestions[displayIndex]?.points ?? 1} point(s)
                </p>

                <div className="grid grid-cols-1 gap-4">
                  {currentOptionOrder.map((origOptionIndex, idx) => (
                    <button
                      type="button"
                      key={`${displayIndex}-${origOptionIndex}`}
                      onClick={() => handleAnswerSelect(origOptionIndex)}
                      className={cn(
                        'p-4 text-left rounded-[var(--radius-md)] border transition-colors duration-[var(--duration-fast)] font-medium text-sm',
                        answers[displayIndex] === origOptionIndex
                          ? 'border-[var(--color-accent)] bg-[var(--color-accent-subtle)] text-[var(--color-text-primary)]'
                          : 'border-[var(--color-border)] bg-[var(--color-surface-1)] hover:border-[var(--color-border-strong)] hover:bg-[var(--color-surface-2)]',
                      )}
                    >
                      <div className="flex items-center gap-4">
                        <span className="w-7 h-7 rounded-[var(--radius-sm)] bg-[var(--color-surface-2)] flex items-center justify-center text-xs font-medium text-[var(--color-text-tertiary)]">
                          {String.fromCharCode(65 + idx)}
                        </span>
                        {scoredQuestions[displayIndex]?.options[origOptionIndex]}
                      </div>
                    </button>
                  ))}
                </div>

                {(quizFinished || secondsLeft === 0) && pageState === 'playing' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="pt-10 flex flex-col items-center gap-3"
                  >
                    <p className="text-sm text-[var(--color-text-secondary)]">Submitting preview answers…</p>
                  </motion.div>
                )}
              </div>
            )}

            {/* ── Review ── */}
            {pageState === 'review' && (
              <AnimatePresence>
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  {/* Score summary */}
                  <div className="space-y-4">
                    <div className="w-16 h-16 bg-[var(--color-success-subtle)] rounded-[var(--radius-lg)] flex items-center justify-center mx-auto text-[var(--color-success)]">
                      <CheckCircle2 className="w-8 h-8" />
                    </div>
                    <h3 className="text-xl font-semibold text-[var(--color-text-primary)]">
                      {currentSession?.label} — Review
                    </h3>
                    <div className="text-3xl font-bold text-[var(--color-accent)] tabular-nums">
                      {totalPoints} / {maxPoints} pts
                    </div>
                    <p className="text-[var(--color-text-secondary)] text-sm">
                      Completed in {formatCountdown(durationSeconds)}
                    </p>
                  </div>

                  {/* Verification warning */}
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-[var(--radius-md)] bg-[var(--color-warning-subtle)] border border-[var(--color-warning)]">
                    <AlertTriangle className="w-3.5 h-3.5 text-[var(--color-warning)]" />
                    <span className="text-xs font-medium text-[var(--color-text-primary)]">
                      Correct answers shown below for verification. Members will NOT see this.
                    </span>
                  </div>

                  {/* Question review list */}
                  <div className="space-y-4 text-left">
                    {scoredQuestions.map((q, qi) => {
                      const userAnswer = answers[qi];
                      const isCorrect = userAnswer === q.correctAnswer;
                      const pointsAwarded = isCorrect ? q.points : 0;

                      return (
                        <div
                          key={q.id}
                          className="p-4 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface-0)]"
                        >
                          <div className="flex items-start justify-between gap-3 mb-3">
                            <h4 className="text-sm font-semibold text-[var(--color-text-primary)] leading-snug">
                              <span className="text-[var(--color-text-tertiary)] mr-1.5">
                                Q{qi + 1}.
                              </span>
                              {q.question}
                            </h4>
                            <span
                              className={cn(
                                'text-xs font-semibold px-2 py-0.5 rounded-[var(--radius-md)] shrink-0 tabular-nums',
                                isCorrect
                                  ? 'bg-[var(--color-success-subtle)] text-[var(--color-success)]'
                                  : 'bg-[var(--color-error-subtle)] text-[var(--color-error)]',
                              )}
                            >
                              {pointsAwarded}/{q.points} pt{q.points !== 1 ? 's' : ''}
                            </span>
                          </div>

                          <QuizQuestionMediaView media={q.media} />

                          <div className="grid grid-cols-1 gap-2">
                            {q.options.map((option, oi) => {
                              const isThisCorrect = oi === q.correctAnswer;
                              const isUserPick = oi === userAnswer;
                              const isWrongPick = isUserPick && !isThisCorrect;

                              return (
                                <div
                                  key={oi}
                                  className={cn(
                                    'flex items-center gap-3 p-3 rounded-[var(--radius-md)] border text-sm',
                                    isThisCorrect
                                      ? 'border-[var(--color-success)] bg-[var(--color-success-subtle)]'
                                      : isWrongPick
                                        ? 'border-[var(--color-error)] bg-[var(--color-error-subtle)]'
                                        : 'border-[var(--color-border)] bg-[var(--color-surface-1)]',
                                  )}
                                >
                                  {isThisCorrect && (
                                    <CheckCircle2 className="w-4 h-4 text-[var(--color-success)] shrink-0" />
                                  )}
                                  {isWrongPick && (
                                    <XCircle className="w-4 h-4 text-[var(--color-error)] shrink-0" />
                                  )}
                                  {!isThisCorrect && !isWrongPick && (
                                    <span className="w-4 h-4 shrink-0" />
                                  )}
                                  <span
                                    className={cn(
                                      'font-medium',
                                      isThisCorrect
                                        ? 'text-[var(--color-success)]'
                                        : isWrongPick
                                          ? 'text-[var(--color-error)]'
                                          : 'text-[var(--color-text-secondary)]',
                                    )}
                                  >
                                    <span className="text-[var(--color-text-tertiary)] mr-1.5">
                                      {String.fromCharCode(65 + oi)}.
                                    </span>
                                    {option}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {surveyQuestion && (
                    <div className="text-left space-y-4 border border-[var(--color-border)] rounded-[var(--radius-lg)] p-4 bg-[var(--color-surface-0)]">
                      <div>
                        <p className="text-xs uppercase tracking-wide text-[var(--color-text-tertiary)]">Post-submit survey</p>
                        <h4 className="text-sm font-semibold text-[var(--color-text-primary)] mt-1">{surveyQuestion.question}</h4>
                      </div>
                      <QuizQuestionMediaView media={surveyQuestion.media} />
                      <div className="grid grid-cols-1 gap-2">
                        {surveyQuestion.options.map((option, idx) => (
                          <button
                            type="button"
                            key={`preview-survey-${idx}`}
                            onClick={() => setSurveyAnswer(idx)}
                            className={cn(
                              'flex items-center gap-3 p-3 rounded-[var(--radius-md)] border text-sm text-left',
                              surveyAnswer === idx
                                ? 'border-[var(--color-accent)] bg-[var(--color-accent-subtle)]'
                                : 'border-[var(--color-border)] bg-[var(--color-surface-1)]',
                            )}
                          >
                            <span className="text-[var(--color-text-tertiary)]">{String.fromCharCode(65 + idx)}.</span>
                            <span className="text-[var(--color-text-primary)]">{option}</span>
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-[var(--color-text-secondary)]">
                        Preview only: this survey is displayed after the scored round and does not affect points or timing.
                      </p>
                    </div>
                  )}

                  {/* Bottom actions */}
                  <div className="flex flex-col items-center gap-3 pt-4">
                    {isCampaignMode && !isLastRound && (
                      <button
                        type="button"
                        onClick={() => void handleNextRound()}
                        className="inline-flex items-center gap-2 bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] text-white font-semibold py-3 px-6 rounded-[var(--radius-md)] transition-colors duration-[var(--duration-fast)]"
                      >
                        Next Round
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    )}

                    {isCampaignMode && isLastRound && (
                      <p className="text-sm text-[var(--color-text-secondary)]">
                        All rounds in this campaign have been previewed.
                      </p>
                    )}

                    <button
                      type="button"
                      onClick={handleBackToAdmin}
                      className="inline-flex items-center gap-2 text-sm font-medium text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors duration-[var(--duration-fast)]"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Back to Admin
                    </button>
                  </div>
                </motion.div>
              </AnimatePresence>
            )}

            {/* ── Campaign Complete ── */}
            {pageState === 'complete' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
              >
                <div className="w-16 h-16 bg-[var(--color-success-subtle)] rounded-[var(--radius-lg)] flex items-center justify-center mx-auto text-[var(--color-success)]">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-semibold text-[var(--color-text-primary)]">
                  All Rounds Complete
                </h3>
                <p className="text-[var(--color-text-secondary)] text-sm">
                  You've previewed all {sortedSessions.length} round{sortedSessions.length !== 1 ? 's' : ''} in{' '}
                  <span className="font-medium text-[var(--color-text-primary)]">
                    {campaign?.title}
                  </span>.
                </p>
                <button
                  type="button"
                  onClick={handleBackToAdmin}
                  className="inline-flex items-center gap-2 bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] text-white font-semibold py-3 px-6 rounded-[var(--radius-md)] transition-colors duration-[var(--duration-fast)]"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Admin
                </button>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
